import {
  ContainerData,
  DivTable,
  FormUpdate,
  ModalContainer,
  ModalHeader,
  Overlay,
} from "./styles";

import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import debounce from "lodash.debounce";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Page } from "@/components/page-base";
import { SupportService } from "./services";

import { MoreHorizontal } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Form from "@/components/Form";
import { Close } from "@mui/icons-material";
import { maskCpfCnpj } from "@/utils";
import { toast } from "react-toastify";

export type User = {
  id: string;
  fullName: string;
  userType: string;
  phone?: string;
  email: string;
  documentData: string;
  createdAt: string;
  isVerified: string;
};

export const Support: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [loading, setLoading] = useState<boolean>();
  const [modalUpdateProfile, setModalUpdateProfile] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [searchTerm, setSearchTerm] = useState("");

  const [totalUsers, setTotalUsers] = useState();
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState();

  const [data, setData] = useState<User[]>([]);

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: "fullName",
      header: "Usuario",
      cell: ({ row }) => <div>{row.getValue("fullName")}</div>,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => <div>{row.getValue("email")}</div>,
    },
    {
      accessorKey: "documentData",
      header: "CPF / CNPJ",
      cell: ({ row }) => (
        <div className="text-nowrap">
          {maskCpfCnpj(row.getValue("documentData"))}
        </div>
      ),
    },
    {
      accessorKey: "createdAt",
      header: () => <div>Data de criação</div>,
      cell: ({ row }) => {
        const date = new Date(row.getValue("createdAt"));
        const formattedDate = date.toLocaleDateString("pt-BR");
        const formattedTime = date.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        });

        return (
          <div className="lowercase text-nowrap">
            {formattedDate} {formattedTime}
          </div>
        );
      },
    },
    {
      accessorKey: "isVerified",
      header: "Situação",
      cell: ({ row }) => (
        <div
          className={cn(
            "capitalize text-center font-bold p-1 text-white rounded text-nowrap",
            {
              "bg-green-700": row.getValue("isVerified") === true,
              "bg-gray-400": row.getValue("isVerified") === false,
            }
          )}
        >
          {row.getValue("isVerified") ? "VERIFICADA" : "EMAIL NÃO VERIFICADO"}
        </div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const user = row.original;

        return (
          <Button
            className="h-8 w-8 p-0 bg-white text-black hover:bg-pcPrimaryColor hover:text-white border-none"
            onClick={() => handleModalUpdate(user)}
          >
            <MoreHorizontal />
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const schemaUpdateForm = z.object({
    fullName: z.string().nonempty("O Nome é obrigatório"),
    phone: z
      .string()
      .nonempty("O Telefone é obrigatório")
      .min(11, { message: "Informe o número completo" }),
    email: z
      .string()
      .nonempty("O email é obrigatório")
      .email("Informe um email válido")
      .transform((email) => email.toLowerCase()),
    documentData: z.string(),
  });

  type updateFormProps = z.infer<typeof schemaUpdateForm>;

  const updateForm = useForm<updateFormProps>({
    resolver: zodResolver(schemaUpdateForm),
  });
  const handleSubmitVerify = async () => {
    if (selectedUser) {
      const updateUser = await SupportService.verifyUser(
        Number(selectedUser.id)
      );

      setData((prevData) => {
        if (prevData && prevData.length > 0) {
          return prevData.map((user) =>
            user.id === selectedUser.id ? { ...user, isVerified: true } : user
          );
        } else {
          return [{ ...updateUser, documentData: selectedUser.documentData }];
        }
      });

      toast.success("Usuario verificado com sucesso!");
      setModalUpdateProfile(false);
    }
  };

  const handleSubmit = async (data: updateFormProps) => {
    if (selectedUser) {
      try {
        const updatedUser = await SupportService.updateUser({
          id: selectedUser.id,
          email: data.email,
          fullName: data.fullName,
          phone: data.phone,
        });

        setData((prevData) => {
          if (prevData && prevData.length > 0) {
            return prevData.map((user) =>
              user.id === updatedUser.id
                ? {
                    ...user,
                    email: data.email,
                    fullName: data.fullName,
                    phone: data.phone,
                  }
                : user
            );
          } else {
            return [
              { ...updatedUser, documentData: selectedUser.documentData },
            ];
          }
        });

        toast.success("Usuário atualizado!");
        setModalUpdateProfile(false);
      } catch (error: any) {
        toast.error(error.message || "Erro ao atualizar usuário");
      }
    }
  };

  const handleModalUpdate = async (data: User) => {
    setModalUpdateProfile(true);
    setSelectedUser(data);
  };

  const handleGetDataUsers = async (page: number) => {
    setSearchTerm("");

    setLoading(true);
    SupportService.getUsers({ page }).then((data) => {
      setData(data.data);
      setTotalUsers(data.meta.total);
      setPage(page);
      setLastPage(data.meta.lastPage);
      setLoading(false);
    });
  };

  useEffect(() => {
    handleGetDataUsers(1);
  }, []);

  useEffect(() => {
    if (selectedUser) {
      updateForm.reset(selectedUser);
    }
  }, [selectedUser]);

  useEffect(() => {
    // Cria uma função debounced para buscar os usuários no backend
    const debouncedFetch = debounce(async () => {
      if (searchTerm === "") {
        handleGetDataUsers(1);
        return;
      }
      try {
        const users = await SupportService.searchUser(searchTerm);
        setData(users);
      } catch (error) {
        console.error("Erro na busca:", error);
      }
    }, 300); // 300ms de debounce

    debouncedFetch();

    // Limpa o debounce ao desmontar ou quando searchTerm mudar
    return () => debouncedFetch.cancel();
  }, [searchTerm]);

  return (
    <>
      <Page.Root>
        <Page.Header title="Support" key="support" />
        <ContainerData>
          <DivTable className="shadow-2xl mt-4 px-10">
            <div className="flex items-center pb-2">
              <Input
                placeholder="Nome, telefone, email, cpf, cnpj..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
              <Button
                className="ml-4 px-4 py-2 bg-pcPrimaryColor hover:bg-pcPrimaryColorHover text-white"
                onClick={() => handleGetDataUsers(1)}
              >
                Limpar filtros
              </Button>
            </div>
            <Table className="rounded shadow">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      Sem informações
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className="flex items-center justify-end space-x-2 py-4">
              <div className="flex-1 text-xs text-muted-foreground text-white">
                Mostrando {(page - 1) * 10} - {page * 10} de {totalUsers}{" "}
                usuario(s)
              </div>
              <div className="space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGetDataUsers(page - 1)}
                  disabled={page === 1}
                >
                  Voltar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGetDataUsers(page + 1)}
                  disabled={page === lastPage}
                >
                  Proxima
                </Button>
              </div>
            </div>
          </DivTable>

          {modalUpdateProfile && (
            <ModalContainer>
              <ModalHeader>
                <div>
                  <strong>Perfil</strong>
                  <span>Editar informações cadastrais</span>
                </div>
                <button
                  className="p-2 text-sm text-white font-bold bg-gray-500 hover:bg-gray-400"
                  onClick={() => setModalUpdateProfile(!modalUpdateProfile)}
                >
                  <Close
                    sx={{
                      width: "30px",
                      height: "30px",
                    }}
                  />
                </button>
              </ModalHeader>
              <FormProvider {...updateForm}>
                <FormUpdate onSubmit={updateForm.handleSubmit(handleSubmit)}>
                  <div className="flex items-center gap-1">
                    {selectedUser?.userType === "COMPANY" && (
                      <Form.Field height={54}>
                        <Form.Label>Razão social</Form.Label>
                        <Form.Input
                          defaultValue={selectedUser?.fullName}
                          name="fullName"
                          maskType="text"
                        />
                        <Form.ErrorMessage field="fullName" />
                      </Form.Field>
                    )}

                    {selectedUser?.userType === "PERSON" && (
                      <Form.Field height={54}>
                        <Form.Label>Nome completo</Form.Label>
                        <Form.Input
                          defaultValue={selectedUser?.fullName}
                          name="fullName"
                          maskType="text"
                        />
                        <Form.ErrorMessage field="fullName" />
                      </Form.Field>
                    )}
                    {selectedUser?.userType === "COMPANY" && (
                      <Form.Field height={54}>
                        <Form.Label>CNPJ</Form.Label>
                        <Form.Input
                          disabled={true}
                          defaultValue={selectedUser?.documentData}
                          className="text-center text-muted-foreground"
                          name="documentData"
                          maskType="cnpj"
                        />
                        <Form.ErrorMessage field="documentData" />
                      </Form.Field>
                    )}

                    {selectedUser?.userType === "PERSON" && (
                      <Form.Field height={54}>
                        <Form.Label>CPF</Form.Label>
                        <Form.Input
                          disabled={true}
                          defaultValue={selectedUser?.documentData}
                          className="text-center text-muted-foreground"
                          placeholder="000.000.000-00"
                          name="documentData"
                          maskType="cpf"
                        />
                        <Form.ErrorMessage field="documentData" />
                      </Form.Field>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <Form.Field height={54}>
                      <Form.Label>Email</Form.Label>
                      <Form.Input
                        name="email"
                        defaultValue={selectedUser?.email}
                        maskType="email"
                      />
                      <Form.ErrorMessage field="email" />
                    </Form.Field>
                    <Form.Field height={54}>
                      <Form.Label>WhatsApp</Form.Label>
                      <Form.Input
                        name="phone"
                        className="text-center"
                        defaultValue={selectedUser?.phone}
                        placeholder="DDD 9 0000-0000"
                        maskType="tel"
                      />
                      <Form.ErrorMessage field="phone" />
                    </Form.Field>
                  </div>

                  <Form.Button
                    type="submit"
                    className="hover:brightness-90 mt-2"
                  >
                    {loading ? "Carregando..." : "Atualizar"}
                  </Form.Button>
                </FormUpdate>
              </FormProvider>
              {!selectedUser?.isVerified && (
                <button
                  className="p-1 rounded w-full mt-1 text-white bg-pcPrimaryColor"
                  onClick={handleSubmitVerify}
                >
                  Verificar Conta
                </button>
              )}
            </ModalContainer>
          )}
        </ContainerData>
      </Page.Root>
      {modalUpdateProfile && (
        <Overlay onClick={() => setModalUpdateProfile(false)} />
      )}
    </>
  );
};
